import { useEffect, useState } from "react"
import { AiOutlinePlus } from "react-icons/ai"
import { Tooltip } from "react-tooltip"
import "react-tooltip/dist/react-tooltip.css"

import { css } from "@emotion/react"
import Stack from "@p-ban/common/src/components/layout/Stack"
import LoadingOverlay from "@p-ban/common/src/components/overlay/LoadingOverlay/LoadingOverlay"
import { isNonEmptyArray } from "@p-ban/common/src/utils/ArrayUtils"

import type { EstimateErrors } from "../../../../../api/estimate/csComEst"
import MOUNT_COURSE from "../../../constant/mountCourse"
import { isRigid } from "../../../service/oneClickIs"
import type EstimateValues from "../../../types/params"
import type { EstimateParams } from "../../../types/params"
import CourseBox from "../../components/1Click/result/CourseBox"
import { RadioGroup, RadioGroupItem } from "../../../../../shadcn/RadioGroup"
import STYLED_BUTTON_ID from "../../constant/styledButtonId"
import useEstimateResult from "../../service/hooks/useEstimateResult"
import DeliveryZeroModal from "../modal/DeliveryZero/DeliveryZeroModal"
import Discount from "./Discount"
import ErrorMessages from "./ErrorMessages"
import FactoryList from "./FactoryList"
import ManufactureResult from "./ManufactureResult"
import ServiceResult from "./ServiceResult"
import PeriodAndAmount from "./TotalResult/PeriodAndAmount"
import TotalResult from "./TotalResult/TotalResult"

import 設計 from "./design.png"
import 実装 from "./mount.png"
import { Separator } from "../../../../../shadcn/Separator"

const graySpinnerStyle = css`
  filter: brightness(0) saturate(100%) invert(50%);
`

type Props = {
  values: EstimateValues
  params: EstimateParams
  setErrors: React.Dispatch<React.SetStateAction<EstimateErrors>>
  openLoginModal: (options: { onMount: () => void; onLogin: () => void; shouldRedirect: boolean }) => void
  enabled: boolean
  startEstimate: () => void
  partsListError?: string
}

const Result = ({ values, params, setErrors, openLoginModal, enabled, startEstimate, partsListError }: Props) => {
  const estimateResult = useEstimateResult(params, setErrors, openLoginModal, enabled)
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)

  useEffect(() => {
    const handleScroll = () => setIsTooltipOpen(false)
    window.addEventListener("scroll", handleScroll, true)
    return () => window.removeEventListener("scroll", handleScroll, true)
  }, [])

  if (!enabled) {
    return (
      <div className="rounded-lg bg-white px-4 py-4">
        <TotalResult buttonText="見積り開始" onClick={startEstimate}>
          <PeriodAndAmount />
        </TotalResult>
      </div>
    )
  }
  /** 部品リスト未登録エラー(バックエンドのvalidation)。
   * フロント側の同エラーと重複するため、エラーリストから除外する目的で使用する */
  const PARTS_LIST_BACKEND_ERROR = "部品リストを登録してください。"

  const { isLoading } = estimateResult
  if (!estimateResult.data || partsListError) {
    // バックエンドで部品リストエラーが発生している場合、フロント側の同エラーだけ表示するようにする
    const allErrors = partsListError
      ? [partsListError, ...(estimateResult.errors || []).filter((error) => error !== PARTS_LIST_BACKEND_ERROR)]
      : estimateResult.errors || []

    return (
      <div className="relative min-h-full w-[332px] rounded-lg bg-white p-2.5">
        {!partsListError && isLoading && (
          <div className="absolute inset-0 z-10">
            <LoadingOverlay theme="light" zIndex={10} spinnerStyle={graySpinnerStyle} />
          </div>
        )}
        {isNonEmptyArray(allErrors) && <ErrorMessages errors={allErrors} />}
        <TotalResult buttonText="注文に進む" isShowSaveEstimate>
          <PeriodAndAmount />
        </TotalResult>
      </div>
    )
  }

  const {
    data,
    onChangeManufactureCourse,
    handleMountChange,
    selectedValue,
    selectedMountValue,
    isMountExpressDisabled,
    resultOnClick,
    saveEstimate,
    openEstimateDetail,
  } = estimateResult
  const { design, manufacture, mount, procurement, discounts, total } = data

  return (
    <>
    <div className="relative">
      <FactoryList design={!!design} manufacture={manufacture} mount={mount} />
      <div
        className="relative w-[332px] overflow-x-hidden overflow-y-auto rounded-lg bg-white p-4 [scrollbar-color:transparent_transparent] [scrollbar-gutter:stable] hover:[scrollbar-color:auto]"
        style={{ maxHeight: "calc(100vh - 88px)" }}
      >
        {isLoading && (
          <div className="absolute inset-0 z-10">
            <LoadingOverlay theme="light" zIndex={10} spinnerStyle={graySpinnerStyle} />
          </div>
        )}
        {design && (
          <>
            <ServiceResult src={設計} title="設計サービス">
              <div className="pl-[29px] pr-[5px]">
                <CourseBox name="設計" days={design.days} amount={design.amount} />
              </div>
            </ServiceResult>
            <Separator />
          </>
        )}
        {manufacture && (
          <>
            <ManufactureResult
              isRigid={isRigid(values)}
              hasMount={!!mount}
              manufacture={manufacture}
              selectedManufactureCourse={selectedValue}
              onChange={onChangeManufactureCourse}
            />
            <Separator />
          </>
        )}
        {mount && (
          <>
            <ServiceResult src={実装} title="部品実装サービス">
              <RadioGroup value={selectedMountValue.value} onValueChange={handleMountChange}>
                <Stack direction="column" spacing={5}>
                  <div>
                    <label className="flex cursor-pointer items-center gap-2">
                      <div>
                        <RadioGroupItem value={MOUNT_COURSE.mount.value} />
                      </div>
                      <div className="flex-1 font-bold">
                        <CourseBox
                          name={MOUNT_COURSE.mount.name}
                          days={mount.days}
                          amount={mount.amount}
                          checked={selectedMountValue.value === MOUNT_COURSE.mount.value}
                          isRadioMode
                        />
                      </div>
                    </label>
                  </div>

                  <div>
                    <label
                      id="mountExpress"
                      className={`flex items-center gap-2 ${isMountExpressDisabled ? "cursor-not-allowed" : "cursor-pointer"}`}
                    >
                      <div>
                        <RadioGroupItem value={MOUNT_COURSE.mountExpress.value} disabled={isMountExpressDisabled} />
                      </div>
                      <div className="flex-1 font-bold">
                        <CourseBox
                          name={MOUNT_COURSE.mountExpress.name}
                          days={null}
                          amount={null}
                          checked={selectedMountValue.value === MOUNT_COURSE.mountExpress.value}
                          isRadioMode
                          disabled={isMountExpressDisabled}
                        />
                      </div>
                    </label>
                  </div>
                </Stack>
              </RadioGroup>
              {procurement && (
                <div className="flex h-[26px] items-center justify-end border-b border-[#e0e0e0] px-[3px] pb-1 pt-[3px] text-sm leading-none">
                  <span className="mr-1 flex items-center text-sm text-[#39b536]">
                    <AiOutlinePlus />
                  </span>
                  <span className="flex-1 text-sm font-medium">部品調達サービス</span>
                  <span className="text-right text-sm">見積依頼必須</span>
                </div>
              )}
            </ServiceResult>
            {procurement && (
              // 部品調達選択時のキャプション
              <div className="pb-4">
                <div className="flex items-end justify-center self-stretch pb-2.5 pt-2">
                  <span className="text-center text-sm font-bold leading-[142%] text-[#32383e]">
                    お届け予定日：部品調達の見積後に確定
                  </span>
                </div>
              </div>
            )}
            <Separator />
          </>
        )}
        {total.isIndividual ? (
          procurement?.isIndividual ? (
            <TotalResult buttonText="見積りを依頼" onClick={resultOnClick} />
          ) : (
            <TotalResult buttonText="見積りを依頼" onClick={resultOnClick}>
              <div className="pb-4 pt-4 text-xl font-semibold leading-[155%] text-[#32383e]">自動見積り対象外</div>
              <div className="pb-4 text-sm font-normal leading-[142%] text-[#ea9a3e]">
                {selectedMountValue.key === MOUNT_COURSE.mountExpress.key ? (
                  <>
                    実装短納期見積は、P板.comスタッフよりご案内いたします。
                    <br />
                    ◆お見積り結果につきましてはメールでのご案内となります。
                  </>
                ) : (
                  <>
                    {total?.errors && total.errors.length > 0 ? (
                      total.errors.map((error, i) => <div key={i}>{error}</div>)
                    ) : (
                      <div>入力された内容で自動見積りが出来なかった為、 P板.comスタッフが対応させていただきます。</div>
                    )}
                  </>
                )}
              </div>
            </TotalResult>
          )
        ) : (
          <TotalResult
            buttonText="注文に進む"
            onClick={resultOnClick}
            isShowSaveEstimate
            id={STYLED_BUTTON_ID.ONE_CLICK_ORDER}
          >
            {discounts && <Discount discounts={discounts} />}
            <PeriodAndAmount result={total} openEstimateDetail={openEstimateDetail} />
          </TotalResult>
        )}
        <DeliveryZeroModal saveEstimate={saveEstimate} />
      </div>
    </div>
    {isMountExpressDisabled && (
      <Tooltip
        anchorId="mountExpress"
        place="left"
        positionStrategy="fixed"
        isOpen={isTooltipOpen}
        setIsOpen={setIsTooltipOpen}
      >
        実装短納期は、最短製造コース・量産選択時にご利用いただけます。
      </Tooltip>
    )}
  </>
  )
}

export default Result
